import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import {
  type CatalogEvent,
  isCatalogEvent,
} from "@/features/catalog/mediators/catalogEvents";
import { createCatalogLayoutMediator } from "@/features/catalog/mediators/catalogLayoutMediator";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { CatalogSkeleton } from "@/features/catalog/ui/CatalogSkeleton";
import { shouldInitializeCatalogLayout } from "@/features/catalog/utils/catalogLayoutRoute";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  useComponentNode,
  useHierarchyLifetime,
} from "@/shared/ui/component-hierarchy/hooks";
import { LoadingScreen } from "@/shared/ui/feedback/Loading";

interface CatalogLayoutInitializerProps {
  children: ReactNode;
  deferChildren: boolean;
  isDesktop: boolean;
  pathname: string;
}

interface CatalogLayoutGateProps {
  children: ReactNode;
  deferChildren: boolean;
  isLayoutInitialized: boolean;
  loadingFallback?: ReactNode;
}

export const CatalogLayoutInitializer: React.FunctionComponent<
  CatalogLayoutInitializerProps
> = ({
  children,
  deferChildren,
  isDesktop,
  pathname,
}: CatalogLayoutInitializerProps) => {
  const isLayoutInitialized = useCatalogStore(
    (state) => state.isLayoutInitialized,
  );
  const columns = useCatalogStore((state) => state.columns);
  const rows = useCatalogStore((state) => state.rows);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const textLines = useCatalogStore((state) => state.textLines);
  const fontScale = useCatalogStore((state) => state.catalogFontScale);
  const initializeLayout = useCatalogStore((state) => state.initializeLayout);
  const initialize = useRef(initializeLayout);
  initialize.current = initializeLayout;
  const [mediator] = useState(() =>
    createCatalogLayoutMediator((event) => {
      initialize.current(event.isDesktop);
    }),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (
      !isCatalogEvent(event) ||
      event.type !== "catalog/layout-initialization-requested"
    ) {
      return "unhandled";
    }
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });

  useLayoutEffect(() => {
    if (!shouldInitializeCatalogLayout(deferChildren, isLayoutInitialized)) {
      return;
    }
    const event: CatalogEvent = {
      type: "catalog/layout-initialization-requested",
      isDesktop,
    };
    node.dispatch(event);
  }, [deferChildren, isDesktop, isLayoutInitialized, node]);

  return (
    <ComponentScope node={node}>
      <CatalogLayoutGate
        deferChildren={deferChildren}
        isLayoutInitialized={isLayoutInitialized}
        loadingFallback={
          pathname === "/" ? (
            <CatalogSkeleton
              columns={columns}
              rows={rows}
              imageSize={imageSize}
              textLines={textLines}
              fontScale={fontScale}
              variant={isDesktop ? "desktop" : "mobile"}
            />
          ) : undefined
        }
      >
        {children}
      </CatalogLayoutGate>
    </ComponentScope>
  );
};

export const CatalogLayoutGate: React.FunctionComponent<
  CatalogLayoutGateProps
> = ({
  children,
  deferChildren,
  isLayoutInitialized,
  loadingFallback,
}: CatalogLayoutGateProps) => {
  if (deferChildren && !isLayoutInitialized) {
    return loadingFallback ?? <LoadingScreen />;
  }

  return <>{children}</>;
};
